import { isDefined, isNotDefined } from '@sgme/fp';
import clsx from 'clsx';
import { type ReactElement, useMemo } from 'react';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { ExpiredBadge } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/ExpiredBadge';
import { SolvingRatePricing } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Pricing/SolvingRatePricing';
import { SolvingUpfrontPricing } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Pricing/SolvingUpfrontPricing';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip';
import { useUserContext } from '@/context/UserContext';
import type { ClientsWay } from '@/store/flexi-forward/state';
import { executeFlexiForwardRfsThunk } from '@/store/flexi-forward/thunks';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { CurrencyIndex } from '@/utils/currencies/types';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

export interface DataToDisplay {
  clientWay: ClientsWay;
  currency1: string | undefined;
  currency2: string | undefined;
  currencyPair: string;
  forwardRate: number;
  notionalCurrencyIndex: CurrencyIndex;
  premiumAmount: number;
  premiumInPercent: number;
  premiumCurrency: string | undefined;
  isPremiumReceive: boolean;
}

interface PricingProps {
  tileId: string;
  isExecutable?: boolean;
}

export function Pricing({ tileId, isExecutable = false }: PricingProps): ReactElement {
  const dispatch = useAppDispatch();

  const { currentUser, tradePermission } = useUserContext();

  const isInternalSales = currentUser.userType === 'Internal Sales';
  const userEmail = currentUser.login;
  const canTrade = tradePermission === 'allowed';
  const showColoredPrices = useAppSelector((state) => state.userPreferences.showColoredPrices);

  const { solvingType } = useFlexiForwardSelector((flexiForward) => {
    const solvable = flexiForward.properties.solvable;
    const solvingTypeFromUi =
      flexiForward.properties?.solvingType === 'solveUpfront' ? 'PremiumPaymentAmount' : 'ForwardRate';

    const solvingType = isDefined(solvable) ? solvable : solvingTypeFromUi;

    return {
      solvingType,
      isSolvedForRate: solvingType === 'ForwardRate',
    };
  });

  const isTCCheckedStatus = useFlexiForwardSelector((flexiForward) => {
    return flexiForward.tradeCapture.status === 'CHECKED';
  });
  const clientsWay = useFlexiForwardSelector((forwardAccumulator) => forwardAccumulator.properties.clientsWay);

  const handleExecution = () => {
    if (isNotDefined(tileId)) {
      return;
    }

    dispatch(executeFlexiForwardRfsThunk({ tileId, userEmail }));
  };

  const displayPrice = useMemo(() => {
    return solvingType === 'PremiumPaymentAmount' ? (
      <SolvingUpfrontPricing isInternalSales={isInternalSales} />
    ) : (
      <SolvingRatePricing isInternalSales={isInternalSales} />
    );
  }, [solvingType, isInternalSales]);

  const isReadyExecutable = isExecutable && isTCCheckedStatus;

  return isExecutable ? (
    <ForbiddenExecutionTooltip placement="right">
      <div
        className={clsx(
          'price-pricing',
          'py-4px',
          'executable-pricing',
          { 'colored-prices': showColoredPrices },
          clientsWay === 'Buy' ? 'buy' : 'sell',
          ...getRateComponentStyling({ executable: isReadyExecutable, cantTrade: !canTrade }),
          { canTrade },
        )}
        onDoubleClick={canTrade && isReadyExecutable ? handleExecution : undefined}
        data-e2e="price-executable"
      >
        {displayPrice}
      </div>
    </ForbiddenExecutionTooltip>
  ) : (
    <div
      className={clsx(
        'price-pricing',
        'py-4px',
        'border-top',
        'border-bottom',
        'indicative-pricing',
        clientsWay === 'Buy' ? 'buy' : 'sell',
        { 'colored-prices': showColoredPrices },
        'bg-lvl2',
        { canTrade },
      )}
      data-e2e="price-indicative"
    >
      <ExpiredBadge />

      {displayPrice}
    </div>
  );
}
